import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../../../../globals";
import HttpManager from "../../../../services/HttpManager";
import { Numero, NumerosCatalogos, NumerosLista } from "../../../../services/v2/numeros.service";
import NumeroDetallePanel, { CALIDAD, LIMITE, hace } from "./numeroDetalle";
import { conectarDesdeApp } from "./conectarApp";
import "./numeros.css";

const errorTexto = (e: any): string => e?.response?.data?.message || e?.message || "Algo falló. Intenta otra vez.";
type Filtro = "activos" | "pendientes" | "woztell" | "apagados" | "todos";

/**
 * Números de WhatsApp (Mensajería, solo superadmin; Esteban 2026-10-06): todo lo de Woztell y más, en un tablero.
 * Arriba el resumen; cada tarjeta dice lo importante de un vistazo y abre el detalle completo.
 */
const NumerosController: React.FC = () => {
	const http = HttpManager.getInstance();
	const sessionCampus: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampus);
	const legacyCampusKey: any = useSelector((state: RootState) => state.currentUserCampus.value);
	const legacyCampusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const campusId: number | null = (legacyCampusKey && legacyCampusRef ? legacyCampusRef[legacyCampusKey]?.id : null) ?? sessionCampus?.id ?? null;

	const [cat, setCat] = useState<NumerosCatalogos | null>(null);
	const [datos, setDatos] = useState<NumerosLista | null>(null);
	const [filtro, setFiltro] = useState<Filtro>("activos");
	const [buscar, setBuscar] = useState<string>("");
	const [abierto, setAbierto] = useState<number | null>(null);
	const [ocupado, setOcupado] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [aviso, setAviso] = useState<string | null>(null);

	const cargar = () => {
		if (!campusId) return;
		http.numerosService.lista(campusId).then((r) => setDatos(r.data)).catch((e) => setError(errorTexto(e)));
	};
	useEffect(() => {
		if (!campusId) return;
		http.numerosService.catalogos(campusId).then((r) => setCat(r.data)).catch((e) => setError(errorTexto(e)));
		cargar();
		const id = Number(new URLSearchParams(window.location.search).get("id") || 0);
		if (id) { setAbierto(id); window.history.replaceState(null, "", window.location.pathname); }
	}, [campusId]);

	const refrescarTodos = async () => {
		if (!campusId) return;
		setOcupado("refrescar"); setError(null);
		try { setDatos((await http.numerosService.refrescarTodos(campusId)).data); setAviso("Estado actualizado con Meta."); }
		catch (e) { setError(errorTexto(e)); } finally { setOcupado(null); }
	};
	const buscarNuevos = async () => {
		if (!campusId) return;
		setOcupado("nuevos"); setError(null);
		try {
			const r = (await http.numerosService.buscarNuevos(campusId)).data;
			setAviso(r.nuevos.length ? "Números nuevos encontrados (quedan apagados hasta configurarlos): " + r.nuevos.join(", ") : "No hay números nuevos en Meta (" + r.revisados + " revisados).");
			if (r.nuevos.length) setFiltro("apagados");
			cargar();
		} catch (e) { setError(errorTexto(e)); } finally { setOcupado(null); }
	};

	// Números que viven en la app WhatsApp Business del teléfono: solo se conectan con la ventana de Meta.
	const conectarApp = async () => {
		if (!campusId || !cat) return;
		setOcupado("app"); setError(null); setAviso(null);
		try {
			const r = await conectarDesdeApp(cat);
			const n = (await http.numerosService.desdeApp(campusId, r)).data;
			setAviso(n.etiqueta || n.telefono ? "Conectado a WAPP: " + (n.etiqueta || n.telefono) + (n.activo ? "." : ". Es nuevo: queda apagado hasta que lo configures.") : "Conectado a WAPP.");
			cargar(); setAbierto(n.id);
		} catch (e) { setError(errorTexto(e)); } finally { setOcupado(null); }
	};

	const lista = useMemo(() => {
		const q = buscar.trim().toLowerCase();
		return (datos?.numeros || []).filter((n) => {
			if (filtro === "activos" && !n.activo) return false;
			if (filtro === "apagados" && n.activo) return false;
			if (filtro === "woztell" && !(n.activo && n.woztell)) return false;
			if (filtro === "pendientes" && !(n.activo && n.revision.some((r) => !r.ok))) return false;
			if (!q) return true;
			return [n.telefono, n.etiqueta, n.nombre, n.dept_nombre, n.plantel, n.territorio].filter(Boolean).join(" ").toLowerCase().includes(q);
		});
	}, [datos, filtro, buscar]);

	if (!campusId) return <React.Fragment />;
	const r = datos?.resumen;

	return (
		<div className="nu">
			<div className="nu-cab">
				<div>
					<h1>Números</h1>
					{datos?.actualizado ? <span className="nu-tenue">Revisado con Meta {hace(datos.actualizado)}</span> : null}
				</div>
				<div className="nu-cab-acciones">
					<button className="nu-btn" disabled={!!ocupado || !cat} onClick={conectarApp}>{ocupado === "app" ? "Conectando con Meta…" : "Conectar número de la app WhatsApp Business"}</button>
					<button className="nu-btn" disabled={!!ocupado} onClick={buscarNuevos}>{ocupado === "nuevos" ? "Buscando en Meta…" : "Buscar números nuevos"}</button>
					<button className="nu-btn pri" disabled={!!ocupado} onClick={refrescarTodos}>{ocupado === "refrescar" ? "Revisando con Meta…" : "Actualizar estado"}</button>
				</div>
			</div>
			{error ? <div className="nu-aviso oro" onClick={() => setError(null)}>{error}</div> : null}
			{aviso ? <div className="nu-aviso ok" onClick={() => setAviso(null)}>{aviso}</div> : null}

			{r ? (
				<div className="nu-resumen">
					<button className={"nu-res" + (filtro === "activos" ? " on" : "")} onClick={() => setFiltro("activos")}><span>Activos</span><b>{r.activos}</b><small>de {r.total}</small></button>
					<div className="nu-res"><span>Calidad</span><b className="nu-semaforo"><i className="ok" />{r.verde}<i className="medio" />{r.amarilla}<i className="mal" />{r.roja}</b></div>
					<button className={"nu-res" + (r.woztell ? " oro" : "") + (filtro === "woztell" ? " on" : "")} onClick={() => setFiltro("woztell")}><span>Siguen en Woztell</span><b>{r.woztell}</b></button>
					<button className={"nu-res" + (r.alertas ? " oro" : "") + (filtro === "pendientes" ? " on" : "")} onClick={() => setFiltro("pendientes")}><span>Cosas por arreglar</span><b>{r.alertas}</b></button>
				</div>
			) : null}

			<div className="nu-filtros">
				<div className="nu-chips" role="tablist">
					{([["activos", "Activos"], ["pendientes", "Por arreglar"], ["woztell", "En Woztell"], ["apagados", "Apagados"], ["todos", "Todos"]] as [Filtro, string][]).map(([k, t]) => (
						<button key={k} role="tab" aria-selected={filtro === k} className={filtro === k ? "on" : ""} onClick={() => setFiltro(k)}>{t}</button>
					))}
				</div>
				<input className="nu-buscar" placeholder="Buscar número, nombre, plantel…" value={buscar} onChange={(e) => setBuscar(e.target.value)} />
			</div>

			{!datos ? <div className="loader" /> : lista.length === 0 ? <div className="nu-vacio">Nada en esta vista.</div> : (
				<div className="nu-grid">
					{lista.map((n) => <Tarjeta key={n.id} n={n} onAbrir={() => setAbierto(n.id)} />)}
				</div>
			)}

			{abierto && cat ? <NumeroDetallePanel campusId={campusId} cat={cat} id={abierto} onClose={() => setAbierto(null)} onCambio={cargar} /> : null}
		</div>
	);
};

const Tarjeta: React.FC<{ n: Numero; onAbrir: () => void }> = ({ n, onAbrir }) => {
	const cal = CALIDAD[n.calidad || "UNKNOWN"] || CALIDAD.UNKNOWN;
	const malos = n.revision.filter((r) => !r.ok);
	return (
		<button className={"nu-tarjeta" + (n.activo ? "" : " apagado")} onClick={onAbrir}>
			<div className="nu-tarjeta-cab">
				<div className="nu-avatar">{n.foto ? <img src={n.foto} alt="" /> : <span>W</span>}<i className={"nu-punto cal-" + cal.c} title={"Calidad " + cal.t.toLowerCase()} /></div>
				<div className="nu-tarjeta-tit">
					<b>{n.etiqueta || n.telefono}</b>
					<span>{n.telefono}</span>
				</div>
				{n.woztell ? <span className="nu-chip oro">Woztell</span> : n.wapp ? <span className="nu-chip ok">WAPP</span> : null}
			</div>
			<div className="nu-tarjeta-meta">
				<span>{n.dept_nombre}</span>{n.plantel || n.territorio ? <span>{n.plantel || n.territorio}</span> : null}
				{n.limite ? <span>{LIMITE[n.limite] || n.limite}</span> : null}
			</div>
			<div className="nu-tarjeta-cifras">
				<div><b>{n.in24.toLocaleString("es-MX")}</b><small>recibidos 24 h</small></div>
				<div><b>{n.out24.toLocaleString("es-MX")}</b><small>enviados 24 h</small></div>
				<div className={n.fallidos24 ? "oro" : ""}><b>{n.fallidos24}</b><small>fallidos</small></div>
			</div>
			<div className="nu-tarjeta-pie">
				<span>Último mensaje {hace(n.ultimo_in)}</span>
				{malos.length ? <span className="nu-pendientes">{malos.length} por arreglar</span> : n.activo ? <span className="nu-bien">Todo en orden</span> : <span>Apagado</span>}
			</div>
		</button>
	);
};

export default NumerosController;
